import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useReduxAuth';
import { useNotification } from './useNotification';
import { useTranslation } from 'react-i18next';
import { hasuraClient } from '../utils/graphqlClient';
import {
  GET_USERS_BY_TEAM_QUERY,
  GET_TEAM_BY_ID_QUERY,
  GET_AVAILABLE_USERS_FOR_TEAM_QUERY,
  GET_ALL_AVAILABLE_USERS_FOR_TEAM_QUERY,
  GET_COMPANIES_FOR_USER_FORM_QUERY,
} from '../graphql/queries';
import {
  ADD_USER_TO_TEAM_MUTATION,
  REMOVE_USER_FROM_TEAM_MUTATION,
  TRANSFER_USER_TO_TEAM_MUTATION,
} from '../graphql/mutations';
import { formatDateForHasura } from '../utils/dateUtils';

export const useTeamMembers = (config = {}) => {
  const { teamId, includeTransferModal = false } = config;
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { showNotification } = useNotification();

  const [teamMembers, setTeamMembers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [team, setTeam] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferData, setTransferData] = useState(null);
  const [isTransferring, setIsTransferring] = useState(false);

  const isRootUser = currentUser?.role === 'ROOT';

  const fetchTeam = useCallback(async () => {
    try {
      const data = await hasuraClient.query(GET_TEAM_BY_ID_QUERY, {
        teamId: parseInt(teamId),
      });
      setTeam(data.teams_by_pk);
    } catch (error) {
      showNotification(t('teams.messages.fetchError') || 'Error fetching team', 'error');
    }
  }, [teamId, t, showNotification]);

  const fetchTeamMembers = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await hasuraClient.query(GET_USERS_BY_TEAM_QUERY, {
        teamId: parseInt(teamId),
      });

      const transformedMembers = data.users.map(user => ({
        ...user,
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        phoneNumber: user.phone_number || '',
        companyName: user.company?.name || 'Unknown',
        titleName: user.title?.title_name || 'No Title',
        title: undefined,
      }));

      setTeamMembers(transformedMembers);
    } catch (error) {
      showNotification(t('teams.messages.fetchError') || 'Error fetching team members', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [teamId, t, showNotification]);

  const fetchAvailableUsers = useCallback(async () => {
    try {
      let data;
      if (isRootUser) {
        data = await hasuraClient.query(GET_ALL_AVAILABLE_USERS_FOR_TEAM_QUERY, {
          excludeTeamId: parseInt(teamId),
        });
      } else {
        data = await hasuraClient.query(GET_AVAILABLE_USERS_FOR_TEAM_QUERY, {
          companyId: currentUser?.companyId,
          excludeTeamId: parseInt(teamId),
        });
      }
      
      setAvailableUsers(data.users || []);
    } catch (error) {
      showNotification(t('teams.messages.fetchError') || 'Error fetching available users', 'error');
    }
  }, [isRootUser, currentUser?.companyId, teamId, t, showNotification]);

  const fetchCompanies = useCallback(async () => {
    if (!isRootUser) return;
    
    try {
      const data = await hasuraClient.query(GET_COMPANIES_FOR_USER_FORM_QUERY);
      setCompanies(data.companies || []);
    } catch (error) {
      showNotification(t('companies.messages.fetchError') || 'Error fetching companies', 'error');
    }
  }, [isRootUser, t, showNotification]);

  // Transfer modal functions
  const openTransferModal = useCallback((user, currentTeam, newTeam) => {
    setTransferData({ user, currentTeam, newTeam });
    setTransferModalOpen(true);
  }, []);

  const closeTransferModal = useCallback(() => {
    setTransferModalOpen(false);
    setTransferData(null);
  }, []);

  const transferUserToTeam = useCallback(async () => {
    if (!transferData) {
      return false;
    }

    try {
      setIsTransferring(true);
      
      const { user, newTeam } = transferData;
      
      const transferDate = new Date();
      transferDate.setDate(transferDate.getDate() + 1);
      const formattedDate = formatDateForHasura(transferDate);

      const data = await hasuraClient.mutate(TRANSFER_USER_TO_TEAM_MUTATION, {
        userId: parseInt(user.id),
        newTeamId: parseInt(newTeam.id),
        transferDate: formattedDate,
      });
      
      if (data.update_users_by_pk) {
        showNotification(t('teams.messages.transferSuccess') || 'User transferred successfully', 'success');
        await fetchTeamMembers();
        await fetchAvailableUsers();
        closeTransferModal();
        // Return true to close the form modal as well
        return true;
      }
      
      return false;
    } catch (error) {
      showNotification(t('teams.messages.transferError') || 'Error occurred during transfer', 'error');
      return false;
    } finally {
      setIsTransferring(false);
    }
  }, [transferData, fetchTeamMembers, fetchAvailableUsers, closeTransferModal, t, showNotification]);

  const addUserToTeam = useCallback(async (formData) => {
    try {
      setIsCreating(true);
      
      const selectedUser = availableUsers.find(u => u.id === parseInt(formData.userId));
      if (!selectedUser) {
        showNotification(t('teams.messages.userNotFound') || 'Seçilen kullanıcı bulunamadı', 'error');
        return false;
      }

      const isCurrentlyTeamManager = selectedUser.team_id && selectedUser.team?.team_manager_id === selectedUser.id;
      
      if (isCurrentlyTeamManager) {
        showNotification(t('teams.messages.managerAlreadyInOtherTeam'), 'error');
        return false;
      }

      if (selectedUser.team_id && selectedUser.team) {
        openTransferModal(selectedUser, selectedUser.team, team);
        return false; 
      }

      const data = await hasuraClient.mutate(ADD_USER_TO_TEAM_MUTATION, {
        userId: parseInt(formData.userId),
        teamId: parseInt(teamId),
      });
      
      if (data.update_users_by_pk) {
        showNotification(t('teams.messages.memberAddSuccess') || 'User added to team successfully', 'success');
        await fetchTeamMembers();
        await fetchAvailableUsers();
        return true;
      }
      
      return false;
    } catch (error) {
      showNotification(t('teams.messages.memberAddError') || 'Error adding user to team', 'error');
      return false;
    } finally {
      setIsCreating(false);
    }
  }, [teamId, availableUsers, team, fetchTeamMembers, fetchAvailableUsers, openTransferModal, t, showNotification]);

  const removeUserFromTeam = useCallback(async (userId) => {
    try {
      setIsDeleting(true);
      
      const data = await hasuraClient.mutate(REMOVE_USER_FROM_TEAM_MUTATION, { 
        userId: parseInt(userId) 
      });
      
      if (data.update_users_by_pk) {
        showNotification(t('teams.messages.memberRemoveSuccess') || 'User removed from team successfully', 'success');
        await fetchTeamMembers();
        await fetchAvailableUsers();
        return true;
      }
      return false;
    } catch (error) {
      showNotification(t('teams.messages.memberRemoveError') || 'Error removing user from team', 'error');
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, [fetchTeamMembers, fetchAvailableUsers, t, showNotification]);

  const handleFormSubmit = useCallback(async (formData, memberId = null) => {
    return await addUserToTeam(formData);
  }, [addUserToTeam]);

  useEffect(() => {
    if (teamId && currentUser?.id) {
      fetchTeam();
      fetchTeamMembers();
      fetchAvailableUsers();
      if (isRootUser) {
        fetchCompanies();
      }
    }
  }, [teamId, currentUser?.id, currentUser?.role, currentUser?.companyId, fetchTeam, fetchTeamMembers, fetchAvailableUsers, fetchCompanies, isRootUser]);

  return {
    teamMembers,
    availableUsers,
    companies,
    team,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isRootUser,
    handleFormSubmit,
    removeUserFromTeam,
    deleteTeamMember: removeUserFromTeam, // Add this for DynamicCrudPage
    fetchTeamMembers,
    fetchAvailableUsers,
    // Additional data for DynamicCrudPage
    additionalData: {
      availableUsers,
      companies,
      team,
    },
    // Transfer modal (only if includeTransferModal is true)
    ...(includeTransferModal && {
    transferModalOpen,
    transferData,
    isTransferring,
    openTransferModal,
    closeTransferModal,
    transferUserToTeam,
    }),
  };
}; 